import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import { getAdminAuditLogsApi } from "../../../api/admin";
import AdminPagination from "./AdminPagination";

const AuditLogs = () => {
  const detail = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    action: "",
    outcome: "",
    start: "",
    end: "",
  });
  const [applied, setApplied] = useState(filters);
  const limit = 25;
  const query = useQuery(
    ["adminAuditLogs", page, applied],
    () => getAdminAuditLogsApi({ page, limit, ...applied }),
    { keepPreviousData: true },
  );
  const data = query.data?.data;
  const apply = () => {
    setPage(1);
    setApplied(filters);
  };
  const clear = () => {
    const empty = {
      search: "",
      category: "",
      action: "",
      outcome: "",
      start: "",
      end: "",
    };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
  };
  const openDetail = (entry) => {
    setSelected(entry);
    detail.onOpen();
  };

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="3" mb="3">
        <Input
          value={filters.search}
          onChange={(event) =>
            setFilters({ ...filters, search: event.target.value })
          }
          placeholder="Search worker, action, summary or record ID"
          onKeyDown={(event) => event.key === "Enter" && apply()}
        />
        <Select
          value={filters.category}
          onChange={(event) =>
            setFilters({ ...filters, category: event.target.value })
          }
        >
          <option value="">All categories</option>
          {(data?.categories || []).map((value) => (
            <option key={value} value={value}>
              {value.replaceAll("_", " ")}
            </option>
          ))}
        </Select>
        <Select
          value={filters.action}
          onChange={(event) =>
            setFilters({ ...filters, action: event.target.value })
          }
        >
          <option value="">All actions</option>
          {(data?.actions || []).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <Select
          value={filters.outcome}
          onChange={(event) =>
            setFilters({ ...filters, outcome: event.target.value })
          }
        >
          <option value="">All outcomes</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </Select>
        <Input
          type="datetime-local"
          value={filters.start}
          onChange={(event) =>
            setFilters({ ...filters, start: event.target.value })
          }
          aria-label="Start date"
        />
        <Input
          type="datetime-local"
          value={filters.end}
          onChange={(event) =>
            setFilters({ ...filters, end: event.target.value })
          }
          aria-label="End date"
        />
      </SimpleGrid>
      <Flex mb="5" gap="2">
        <Button onClick={apply}>Apply filters</Button>
        <Button variant="outline" onClick={clear}>
          Clear
        </Button>
      </Flex>

      {query.isLoading ? (
        <Center minH="260px">
          <Spinner />
        </Center>
      ) : query.isError ? (
        <Center minH="240px">
          <Text color="red.500">Unable to load audit logs.</Text>
        </Center>
      ) : !data?.auditLogs?.length ? (
        <Center
          minH="180px"
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="xl"
        >
          <Text color="gray.500">No audit events match these filters.</Text>
        </Center>
      ) : (
        <TableContainer overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Timestamp</Th>
                <Th>Worker</Th>
                <Th>Role</Th>
                <Th>Category</Th>
                <Th>Action</Th>
                <Th>Summary</Th>
                <Th>Outcome</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.auditLogs.map((entry) => (
                <Tr
                  key={entry._id}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => openDetail(entry)}
                >
                  <Td whiteSpace="nowrap">
                    {new Date(entry.createdAt).toLocaleString()}
                  </Td>
                  <Td>
                    <Text fontWeight="700">
                      {entry.actorSnapshot?.name || "Unauthenticated"}
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      {entry.actorSnapshot?.email || "-"}
                    </Text>
                  </Td>
                  <Td>{entry.actorSnapshot?.role || "-"}</Td>
                  <Td textTransform="capitalize">
                    {entry.category?.replaceAll("_", " ")}
                  </Td>
                  <Td fontFamily="mono" fontSize="xs">
                    {entry.action}
                  </Td>
                  <Td maxW="320px" whiteSpace="normal">
                    {entry.summary}
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        entry.outcome === "success" ? "green" : "red"
                      }
                    >
                      {entry.outcome}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <AdminPagination
        page={page}
        limit={limit}
        total={data?.total}
        onChange={setPage}
      />

      <Modal
        isOpen={detail.isOpen}
        onClose={detail.onClose}
        size="3xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Audit event details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            {selected && (
              <Stack spacing="5">
                <SimpleGrid columns={{ base: 1, md: 2 }} gap="3">
                  {[
                    [
                      "Timestamp",
                      new Date(selected.createdAt).toLocaleString(),
                    ],
                    ["Action", selected.action],
                    ["Category", selected.category],
                    ["Outcome", selected.outcome],
                    [
                      "Worker",
                      selected.actorSnapshot?.name || "Unauthenticated",
                    ],
                    ["Role", selected.actorSnapshot?.role || "Not available"],
                    [
                      "Entity",
                      selected.entityType
                        ? `${selected.entityType} · ${selected.entityId || ""}`
                        : "Not available",
                    ],
                    [
                      "Request ID",
                      selected.context?.requestId || "Not available",
                    ],
                  ].map(([label, value]) => (
                    <Box key={label} bg="gray.50" borderRadius="lg" p="3">
                      <Text color="gray.500" fontSize="xs" fontWeight="700">
                        {label.toUpperCase()}
                      </Text>
                      <Text mt="1" fontSize="sm" overflowWrap="anywhere">
                        {value}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
                <Box>
                  <Text fontWeight="800" fontSize="sm">
                    Summary
                  </Text>
                  <Text mt="1">{selected.summary}</Text>
                </Box>
                {!!selected.changes?.length && (
                  <Box>
                    <Text fontWeight="800" fontSize="sm">
                      Changed fields
                    </Text>
                    <Stack mt="2" spacing="2">
                      {selected.changes.map((change, index) => (
                        <Flex
                          key={`${change.field}-${index}`}
                          borderWidth="1px"
                          borderRadius="lg"
                          p="3"
                          justify="space-between"
                          gap="3"
                        >
                          <Text fontWeight="700">{change.field}</Text>
                          <Text color="gray.500" fontSize="sm">
                            {change.from !== undefined
                              ? `${String(change.from)} → `
                              : ""}
                            {change.to !== undefined
                              ? String(change.to)
                              : "Changed"}
                          </Text>
                        </Flex>
                      ))}
                    </Stack>
                  </Box>
                )}
                {!!Object.keys(selected.metadata || {}).length && (
                  <Box>
                    <Text fontWeight="800" fontSize="sm">
                      Event metadata
                    </Text>
                    <Box
                      as="pre"
                      mt="2"
                      bg="gray.50"
                      borderRadius="lg"
                      p="3"
                      fontSize="xs"
                      overflowX="auto"
                      whiteSpace="pre-wrap"
                    >
                      {JSON.stringify(selected.metadata, null, 2)}
                    </Box>
                  </Box>
                )}
                <Box>
                  <Text fontWeight="800" fontSize="sm">
                    Request context
                  </Text>
                  <Text mt="2" color="gray.600" fontSize="sm">
                    {selected.context?.method} {selected.context?.route}
                  </Text>
                  <Text color="gray.500" fontSize="xs">
                    IP: {selected.context?.ipAddress || "-"} · User agent:{" "}
                    {selected.context?.userAgent || "-"}
                  </Text>
                </Box>
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AuditLogs;
